import { useState } from 'react';
import { Header, IconUser, Link, Logo, logoFi, logoSv } from 'hds-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useMatch, useLocation, useNavigate } from 'react-router-dom';
import { $enum } from 'ts-enum-util';
import {
  getMatchingRouteKey,
  useLocalizedRoutes,
  HANKETUNNUS_REGEXP,
  APPLICATION_ID_REGEXP,
  USER_ID_REGEXP,
} from '../../hooks/useLocalizedRoutes';
import useUser from '../../../domain/auth/useUser';
import { Language, LANGUAGES } from '../../types/language';
import { SKIP_TO_ELEMENT_ID } from '../../constants/constants';
import { useFeatureFlags } from '../featureFlags/FeatureFlagsContext';
import HankeCreateDialog from '../../../domain/hanke/hankeCreateDialog/HankeCreateDialog';
import JohtoselvitysCreateDialog from '../../../domain/johtoselvitys/johtoselvitysCreateDialog/JohtoselvitysCreateDialog';
import useIsAuthenticated from '../../../domain/auth/useIsAuthenticated';

// Remove persisted functional form drafts when navigating away via header links
function clearFunctionalPersistence() {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      // patterns for draft and step persistence keys
      if (
        key.startsWith('functional-hanke-form-') ||
        key.startsWith('functional-hanke-form-step-') ||
        key.startsWith('functional-application-form-') ||
        key.startsWith('functional-application-form-step-')
      ) {
        toRemove.push(key);
      }
    }
    for (const k of toRemove) {
      sessionStorage.removeItem(k);
    }
  } catch {
    // ignore storage errors
  }
}

const languageLabels = {
  fi: 'Suomi',
  en: 'English',
  sv: 'Svenska',
};

function HaitatonHeader() {
  const { HOME, PUBLIC_HANKKEET, HANKEPORTFOLIO, JOHTOSELVITYSHAKEMUS, WORKINSTRUCTIONS } =
    useLocalizedRoutes();
  const { t, i18n } = useTranslation();
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();
  const features = useFeatureFlags();
  const logoSrc = i18n.language === 'sv' ? logoSv : logoFi;
  const [showHankeCreateDialog, setShowHankeCreateDialog] = useState(false);
  const [showJohtoselvitysCreateDialog, setShowJohtoselvitysCreateDialog] = useState(false);

  const isMapPath = useMatch({
    path: PUBLIC_HANKKEET.path,
    end: false,
  });
  const isCableReportApplicationPath = useMatch({
    path: JOHTOSELVITYSHAKEMUS.path,
    end: true,
  });
  const isHankePortfolioPath = useMatch({
    path: HANKEPORTFOLIO.path,
    end: false,
  });
  const isWorkInstructionsPath = useMatch({
    path: WORKINSTRUCTIONS.path,
    end: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  function getPathForLanguage(lang: Language, routeKey: string): string {
    const hankeTunnusMatches = HANKETUNNUS_REGEXP.exec(location.pathname);
    const hankeTunnus = hankeTunnusMatches?.[0];
    const applicationIdMatches = APPLICATION_ID_REGEXP.exec(location.pathname);
    const applicationId = applicationIdMatches?.[0];
    const userIdMatches = USER_ID_REGEXP.exec(location.pathname);
    const userId = userIdMatches?.[0];

    let path = lang + t(`routes:${routeKey}.path`);
    if (hankeTunnus) {
      path = path.replace(':hankeTunnus', hankeTunnus);
    }
    if (userId) {
      path = path.replace(':id', userId);
    }
    if (applicationId) {
      path = path.replace(':id', applicationId);
    }
    return path;
  }

  async function setLanguage(lang: string) {
    const routeKey = getMatchingRouteKey(i18n, i18n.language as Language, location.pathname);
    // Determine any trailing subpath after the matched route base so we can preserve
    // multipage/subpage state (e.g. /manual/:id or /workInstructions/card/123).
    //
    // Note: compute everything relative to the path *without* the locale prefix.
    // The localized base path (translation) already contains the route segment
    // (for example `/manual` vs `/manual-en`), so measuring the suffix on the
    // path-without-locale avoids duplicating the base segment when we later
    // append the suffix to the newly computed localized path.
    const baseNoLocale = t(`routes:${routeKey}.path`);
    const localePrefix = `/${i18n.language}`;
    const pathNoLocale = location.pathname.startsWith(localePrefix)
      ? location.pathname.substring(localePrefix.length)
      : location.pathname;
    let suffix = '';
    if (pathNoLocale.startsWith(baseNoLocale)) {
      suffix = pathNoLocale.substring(baseNoLocale.length);
    } else {
      const idx = pathNoLocale.indexOf(baseNoLocale);
      if (idx >= 0) suffix = pathNoLocale.substring(idx + baseNoLocale.length);
    }

    // Fire event so forms can persist state immediately before unmount
    try {
      globalThis.dispatchEvent(
        new CustomEvent('haitaton:languageChanging', { detail: { from: i18n.language, to: lang } }),
      );
    } catch {
      // ignore
    }

    await i18n.changeLanguage(lang);
    const path = getPathForLanguage(lang as Language, routeKey);
    // Append suffix (may include leading '/') so the user remains on the same multipage subpage
    navigate(`${path}${suffix}`);
  }

  function getUserMenuLabel() {
    return user?.profile.name ?? (user?.profile.email as string);
  }

  function openHankeCreateDialog() {
    setShowHankeCreateDialog(true);
  }

  function closeHankeCreateDialog() {
    setShowHankeCreateDialog(false);
  }

  function openJohtoselvitysCreateDialog() {
    setShowJohtoselvitysCreateDialog(true);
  }

  function closeJohtoselvitysCreateDialog() {
    setShowJohtoselvitysCreateDialog(false);
  }

  return (
    <Header
      languages={$enum(LANGUAGES).map((lang) => ({
        label: languageLabels[lang],
        value: lang,
        isPrimary: true,
      }))}
      onDidChangeLanguage={setLanguage}
      defaultLanguage={i18n.language}
    >
      <Header.SkipLink
        skipTo={`#${SKIP_TO_ELEMENT_ID}`}
        label={t('common:components:header:skipToContentLabel')}
      />
      <Header.ActionBar
        title="Haitaton"
        titleHref={HOME.path}
        logoHref={HOME.path}
        frontPageLabel={t('common:components:header:frontPageLabel')}
        logo={<Logo src={logoSrc} alt={t('common:logoAlt')} />}
        menuButtonAriaLabel={t('common:ariaLabels:menuToggle')}
      >
        <Header.LanguageSelector />
        {isAuthenticated && (
          <Header.ActionBarItem
            label={getUserMenuLabel()}
            fixedRightPosition
            preventButtonResize
            icon={<IconUser />}
            id="action-bar-user-menu"
            closeLabel={t('common:ariaLabels:closeButtonLabelText')}
          >
            <Header.LogoutSubmenuButton
              id="log-out-button"
              label={t('authentication:logoutButton')}
              errorLabel={t('authentication:logoutError')}
              errorText={t('authentication:logoutError')}
              errorCloseAriaLabel={t('common:ariaLabels:closeButtonLabelText')}
              loggingOutText={t('authentication:loggingOut')}
            />
          </Header.ActionBarItem>
        )}
        <Header.LoginButton
          id="login-button"
          label={t('authentication:loginButton')}
          errorLabel={t('authentication:loggingInErrorLabel')}
          errorText={t('authentication:genericError')}
          errorCloseAriaLabel={t('common:ariaLabels:closeButtonLabelText')}
          loggingInText={t('authentication:loggingIn')}
          redirectionProps={{ language: i18n.language }}
          fixedRightPosition
        />
      </Header.ActionBar>

      {isAuthenticated && (
        <Header.NavigationMenu>
          {features.publicHankkeet && (
            <Header.Link
              label={PUBLIC_HANKKEET.label}
              as={NavLink}
              to={PUBLIC_HANKKEET.path}
              active={Boolean(isMapPath)}
              onClick={() => clearFunctionalPersistence()}
            />
          )}
          {features.hanke && (
            <Header.Link
              label={t('homepage:hanke:titlePublicArea')}
              as={NavLink}
              to="#"
              onClick={openHankeCreateDialog}
              data-testid="hankeLink"
            />
          )}
          <Header.Link
            label={t('homepage:johtotietoselvitys:title')}
            as={NavLink}
            to="#"
            onClick={openJohtoselvitysCreateDialog}
            active={Boolean(isCableReportApplicationPath)}
            data-testid="cableReportApplicationLink"
          />
          <Header.Link
            label={HANKEPORTFOLIO.label}
            as={NavLink}
            to={HANKEPORTFOLIO.path}
            active={Boolean(isHankePortfolioPath)}
            onClick={() => clearFunctionalPersistence()}
            data-testid="hankeListLink"
          />
          {features.hanke ? (
            <Header.Link
              label={t('routes:WORKINSTRUCTIONS:headerLabel')}
              as={NavLink}
              to={WORKINSTRUCTIONS.path}
              active={Boolean(isWorkInstructionsPath)}
              onClick={() => clearFunctionalPersistence()}
            >
              {t('routes:WORKINSTRUCTIONS:headerLabel')}
            </Header.Link>
          ) : (
            <Header.Link
              label={t('routes:WORKINSTRUCTIONS:headerLabel')}
              as={Link}
              href={t('workInstructions:sideNav:externalLinks:permitsAndInstructions:url')}
              external
              openInNewTab
            >
              {t('routes:WORKINSTRUCTIONS:headerLabel')}
            </Header.Link>
          )}
        </Header.NavigationMenu>
      )}
      <HankeCreateDialog isOpen={showHankeCreateDialog} onClose={closeHankeCreateDialog} />
      <JohtoselvitysCreateDialog
        isOpen={showJohtoselvitysCreateDialog}
        onClose={closeJohtoselvitysCreateDialog}
      />
    </Header>
  );
}

export default HaitatonHeader;
